import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

export const useUtensilCheckSocket = (url) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [dishStatus, setDishStatus] = useState({
    np1_state: "Unknown",
    np2_state: "Unknown",
    details: {},
    last_update: null,
    progress: null,
    is_final: false,
  });
  const socketRef = useRef(null);
  const connectingPromiseRef = useRef(null);
  const activeDetectionRef = useRef(null);

  const connect = useCallback(() => {
    // Don't connect if URL is null or undefined
    if (!url) {
      console.log("Utensil check socket URL is null, skipping connection");
      setIsConnected(false);
      setConnectionError(null);
      return Promise.resolve(false);
    }

    // If we already have a socket and it's connected, do nothing
    if (socketRef.current?.connected) {
      return Promise.resolve(true);
    }

    // If we're already attempting to connect, return the existing promise
    if (connectingPromiseRef.current) {
      return connectingPromiseRef.current;
    }

    // Create a new promise for the connection attempt
    connectingPromiseRef.current = new Promise((resolve, reject) => {
      console.log("Creating new socket connection to:", url);

      // Create socket instance
      socketRef.current = io(url, {
        transports: ["websocket"],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 30000, // 30 seconds timeout
      });

      // Set up a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (!socketRef.current?.connected) {
          const error = new Error("Socket connection timeout");
          console.error(error);
          setConnectionError("Connection timeout after 10 seconds");
          reject(error);
        }
      }, 10000);

      // Handle successful connection
      socketRef.current.on("connect", () => {
        console.log("Socket connected to utensil check server");
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        setConnectionError(null);
        resolve(true);
      });

      // Handle connection errors
      socketRef.current.on("connect_error", (error) => {
        console.error("Socket connection error:", error.message);
        setIsConnected(false);
        setConnectionError(error.message);
        // Don't reject here, let the reconnection attempts work
      });

      // Handle disconnection
      socketRef.current.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
        setIsConnected(false);
        if (reason !== "io client disconnect") {
          setConnectionError("Connection lost: " + reason);
        }
      });

      // Listen for dish status updates
      socketRef.current.on("dish_status", (data) => {
        console.log("Received dish status update:", data);
        setDishStatus(data);

        // Store the latest data in a global variable for components to access
        window.socketResponse = data;
      });

      // Listen for status messages
      socketRef.current.on("status", (data) => {
        console.log("Server status message:", data.message);
      });
    });

    // Clear the connecting promise reference when it settles
    connectingPromiseRef.current
      .catch(() => {})
      .finally(() => {
        connectingPromiseRef.current = null;
      });

    return connectingPromiseRef.current;
  }, [url]);

  // Helper function to ensure socket is connected before proceeding
  const ensureConnected = useCallback(async () => {
    if (socketRef.current?.connected) {
      return true;
    }

    try {
      // Attempt to connect
      await connect();
      return socketRef.current?.connected || false;
    } catch (error) {
      console.error("Failed to ensure socket connection:", error);
      return false;
    }
  }, [connect]);

  // Helper function to check if a NutriPot is detected based on state string
  const isNutriPotDetected = (stateString, utensilType) => {
    if (!stateString) return false;

    // For NP1, consider PRESENT as detected
    if (utensilType === "np1") {
      return stateString === "NP1-PRESENT";
    }

    // For NP2, consider both PRESENT and LID-ON as detected
    if (utensilType === "np2") {
      return stateString === "NP2-PRESENT" || stateString === "NP2-LID-ON";
    }

    return false;
  };

  // Function to stop an ongoing detection process
  const stopDetection = useCallback(() => {
    if (activeDetectionRef.current) {
      clearTimeout(activeDetectionRef.current);
      activeDetectionRef.current = null;
    }

    // Reset detection status in the local state
    setDishStatus({
      np1_state: "Unknown",
      np2_state: "Unknown",
      details: {},
      last_update: null,
      progress: null,
      is_final: false,
    });

    // Also reset the global variable if it exists
    if (typeof window !== "undefined") {
      window.socketResponse = null;
    }

    // If socket is connected, tell server to stop detection
    if (socketRef.current?.connected) {
      socketRef.current.emit("stop_detection", (response) => {
        console.log("Detection stopped:", response);
      });
    }
  }, []);

  const checkUtensil = useCallback(
    async (utensilType) => {
      // Stop any ongoing detection first
      stopDetection();

      // First ensure we have a connected socket
      const isConnected = await ensureConnected();
      if (!isConnected) {
        throw new Error("Cannot connect to utensil check server");
      }

      return new Promise((resolve, reject) => {
        console.log(`Checking utensil: ${utensilType}`);

        // Start the detection process and get status in one combined call
        socketRef.current.emit("start_detection_and_get_status", (response) => {
          console.log("Started detection and getting status:", response);
        });

        // Set a timeout to get the final result
        activeDetectionRef.current = setTimeout(() => {
          // Get the current dish status
          socketRef.current.emit("get_status", (data) => {
            console.log("Got final dish status:", data);

            if (!data) {
              reject(new Error("Invalid dish status response"));
              return;
            }

            // Update the local state
            setDishStatus(data);

            // Store the latest data in a global variable for components to access
            window.socketResponse = data;

            // Check if the requested utensil is detected using new status format
            const isDetected = isNutriPotDetected(
              utensilType === "np1" ? data.np1_state : data.np2_state,
              utensilType
            );

            console.log(
              `${utensilType} detection result:`,
              isDetected,
              `(state: ${
                utensilType === "np1" ? data.np1_state : data.np2_state
              })`
            );

            // Clear the active detection reference
            activeDetectionRef.current = null;

            resolve(isDetected);
          });
        }, 30000); // 30 seconds for detection to complete

        // Set up a one-time listener for immediate results
        const dishStatusHandler = (data) => {
          // Check if this is a final status update with is_final flag
          if (data && data.is_final === true) {
            console.log(`Received final dish status update:`, data);

            // Update local state
            setDishStatus(data);

            // Store the latest data in a global variable for components to access
            window.socketResponse = data;

            // Check if the requested utensil is detected using new status format
            const isDetected = isNutriPotDetected(
              utensilType === "np1" ? data.np1_state : data.np2_state,
              utensilType
            );

            // Clear the timeout and resolve early if we got results
            if (activeDetectionRef.current) {
              clearTimeout(activeDetectionRef.current);
              activeDetectionRef.current = null;
            }

            // Remove the listener after getting results
            socketRef.current.off("dish_status", dishStatusHandler);

            console.log(
              `${utensilType} detection result (final):`,
              isDetected,
              `(state: ${
                utensilType === "np1" ? data.np1_state : data.np2_state
              })`
            );
            resolve(isDetected);
          }
        };

        // Add the temporary listener
        socketRef.current.on("dish_status", dishStatusHandler);

        // Make sure to clean up if the promise is rejected
        return () => {
          if (activeDetectionRef.current) {
            clearTimeout(activeDetectionRef.current);
            activeDetectionRef.current = null;
          }
          socketRef.current.off("dish_status", dishStatusHandler);
        };
      });
    },
    [ensureConnected, stopDetection]
  );

  const resetSocket = useCallback(() => {
    stopDetection();

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    connectingPromiseRef.current = null;
    return connect();
  }, [connect, stopDetection]);

  useEffect(() => {
    // Only connect if URL is provided
    if (url) {
      // Connect when the component mounts or URL changes
      connect().catch((error) => {
        console.error("Initial socket connection failed:", error);
      });
    } else {
      // If URL is null, ensure we're disconnected
      setIsConnected(false);
      setConnectionError(null);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }

    // Clean up when component unmounts or URL changes
    return () => {
      stopDetection();

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      connectingPromiseRef.current = null;
    };
  }, [url, connect, stopDetection]);

  return {
    socket: socketRef.current,
    isConnected,
    connectionError,
    dishStatus,
    checkUtensil,
    resetSocket,
    ensureConnected,
    stopDetection,
  };
};
